(() => {
  'use strict';

  /*
   * Pawkemon Halloween Effects 2026
   * Only runs on the Shopify homepage.
   */

  const REDUCED_MOTION = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  const FINE_POINTER = window.matchMedia(
    '(hover: hover) and (pointer: fine)'
  );

  let effectsRoot = null;
  let ghostObserver = null;
  let ghostHasPlayed = false;
  let spiderReturnTimer = null;

  function isHomepage() {
    return (
      document.body.classList.contains('template-index') ||
      document.querySelector('main[data-template-name="index"]') !== null ||
      window.location.pathname === '/' ||
      window.location.pathname === ''
    );
  }

  function batSvg() {
    return `
      <svg viewBox="0 0 84 44" aria-hidden="true">
        <g class="pawkemon-bat__wings">
          <path
            fill="currentColor"
            d="M3 23
               C11 10 20 5 29 14
               C32 8 36 5 42 5
               C48 5 52 8 55 14
               C64 5 73 10 81 23
               C70 19 61 21 53 28
               C47 34 37 34 31 28
               C23 21 14 19 3 23Z"
          />
        </g>
      </svg>
    `;
  }

  function webSvg() {
    return `
      <svg viewBox="0 0 120 120" aria-hidden="true">
        <g
          fill="none"
          stroke="currentColor"
          stroke-width="1.55"
          stroke-linecap="round"
          opacity="0.96"
        >
          <path d="M0 0H120M0 0V120"/>
          <path d="M0 0L107 107"/>
          <path d="M0 0L118 56"/>
          <path d="M0 0L57 118"/>
          <path d="M24 0C24 14 14 24 0 24"/>
          <path d="M48 0C48 28 28 48 0 48"/>
          <path d="M74 0C74 43 43 74 0 74"/>
          <path d="M102 0C102 60 60 102 0 102"/>
        </g>
      </svg>
    `;
  }

  function spiderSvg() {
    return `
      <svg viewBox="0 0 30 30" aria-hidden="true">
        <g
          fill="none"
          stroke="currentColor"
          stroke-width="1.65"
          stroke-linecap="round"
        >
          <path d="M10 12L4 8M9 15H2M10 18L4 23"/>
          <path d="M20 12L26 8M21 15H28M20 18L26 23"/>
        </g>
        <circle cx="15" cy="10" r="4" fill="currentColor"/>
        <ellipse cx="15" cy="18" rx="6" ry="6.5" fill="currentColor"/>
        <circle cx="13" cy="17" r="0.8" fill="#fff8fd"/>
        <circle cx="17" cy="17" r="0.8" fill="#fff8fd"/>
      </svg>
    `;
  }

  function graveSvg(label) {
    return `
      <svg viewBox="0 0 60 76" aria-hidden="true">
        <path
          fill="currentColor"
          d="M10 68V29
             C10 13 20 5 30 5
             C40 5 50 13 50 29
             V68Z"
        />
        <path
          fill="none"
          stroke="#f5dceb"
          stroke-width="2"
          stroke-linecap="round"
          d="M17 67V30
             C17 19 23 13 30 13
             C37 13 43 19 43 30
             V67"
          opacity="0.55"
        />
        <text
          x="30"
          y="39"
          text-anchor="middle"
          font-size="${label === 'RIP STRESS' ? '7' : '8'}"
          font-family="Arial, sans-serif"
          font-weight="700"
          fill="#f5dceb"
        >${label}</text>
        <path
          fill="#4a3852"
          d="M4 66C13 61 20 65 27 63
             C36 60 45 63 56 66V74H4Z"
        />
      </svg>
    `;
  }

  function ghostSvg() {
    return `
      <svg viewBox="0 0 80 92" aria-hidden="true">
        <path
          fill="currentColor"
          d="M8 82V42
             C8 19 21 7 40 7
             C59 7 72 19 72 42
             V82
             L62 73
             L52 83
             L40 73
             L28 83
             L18 73Z"
        />
        <ellipse cx="30" cy="43" rx="3.5" ry="5" fill="#332039"/>
        <ellipse cx="50" cy="43" rx="3.5" ry="5" fill="#332039"/>
        <path
          d="M34 56C38 60 42 60 46 56"
          fill="none"
          stroke="#332039"
          stroke-width="2"
          stroke-linecap="round"
        />
        <circle cx="21" cy="54" r="5" fill="#f3a9c2" opacity="0.62"/>
        <circle cx="59" cy="54" r="5" fill="#f3a9c2" opacity="0.62"/>
      </svg>
    `;
  }

  function createBat(className) {
    const bat = document.createElement('span');
    bat.className = className;
    bat.innerHTML = batSvg();
    return bat;
  }

  function buildEffects() {
    if (effectsRoot || !isHomepage()) return;

    effectsRoot = document.createElement('div');
    effectsRoot.className = 'pawkemon-halloween-effects';
    effectsRoot.setAttribute('aria-hidden', 'true');

    const leftWeb = document.createElement('div');
    leftWeb.className =
      'pawkemon-halloween-web pawkemon-halloween-web--left';
    leftWeb.innerHTML = webSvg();

    const rightWeb = document.createElement('div');
    rightWeb.className =
      'pawkemon-halloween-web pawkemon-halloween-web--right';
    rightWeb.innerHTML = webSvg();

    const spider = document.createElement('div');
    spider.className = 'pawkemon-spider';
    spider.innerHTML = `
      <span class="pawkemon-spider__thread"></span>
      <span class="pawkemon-spider__body">${spiderSvg()}</span>
    `;

    const batGroupOne = document.createElement('div');
    batGroupOne.className =
      'pawkemon-bat-group pawkemon-bat-group--one';
    batGroupOne.append(
      createBat('pawkemon-bat pawkemon-bat--large'),
      createBat('pawkemon-bat pawkemon-bat--small')
    );

    const batGroupTwo = document.createElement('div');
    batGroupTwo.className =
      'pawkemon-bat-group pawkemon-bat-group--two';
    batGroupTwo.append(
      createBat('pawkemon-bat pawkemon-bat--small'),
      createBat('pawkemon-bat'),
      createBat('pawkemon-bat pawkemon-bat--small')
    );

    const leftGrave = document.createElement('div');
    leftGrave.className =
      'pawkemon-grave pawkemon-grave--left';
    leftGrave.innerHTML = graveSvg('RIP STRESS');

    const rightGrave = document.createElement('div');
    rightGrave.className =
      'pawkemon-grave pawkemon-grave--right';
    rightGrave.innerHTML = graveSvg('SOLD OUT');

    effectsRoot.append(
      leftWeb,
      rightWeb,
      spider,
      batGroupOne,
      batGroupTwo,
      leftGrave,
      rightGrave
    );

    document.body.appendChild(effectsRoot);

    setupSpiderInteraction(spider);
    setupGhost();
  }

  function setupSpiderInteraction(spider) {
    if (!spider || !FINE_POINTER.matches) return;

    spider.addEventListener('mouseenter', () => {
      spider.classList.add('is-frightened');

      window.clearTimeout(spiderReturnTimer);

      spiderReturnTimer = window.setTimeout(() => {
        spider.classList.remove('is-frightened');
      }, 3500);
    });
  }

  function normalizedText(element) {
    return (element?.textContent || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function findHalloweenSection() {
    const sections = Array.from(
      document.querySelectorAll(
        '.shopify-section, section[id^="shopify-section"]'
      )
    );

    return sections.find((section) => {
      const headings = section.querySelectorAll(
        'h1, h2, h3, .title, .collection__title'
      );

      return Array.from(headings).some((heading) => {
        const text = normalizedText(heading);

        return (
          text.includes('halloween specials') ||
          text.includes('halloween special')
        );
      });
    });
  }

  function setupGhost() {
    if (REDUCED_MOTION.matches || ghostHasPlayed) return;

    if (ghostObserver) {
      ghostObserver.disconnect();
    }

    const halloweenSection = findHalloweenSection();
    if (!halloweenSection) return;

    if (
      window.getComputedStyle(halloweenSection).position === 'static'
    ) {
      halloweenSection.style.position = 'relative';
    }

    halloweenSection.style.overflow = 'hidden';

    let ghost = halloweenSection.querySelector(
      '.pawkemon-ghost-peek'
    );

    if (!ghost) {
      ghost = document.createElement('div');
      ghost.className = 'pawkemon-ghost-peek';
      ghost.setAttribute('aria-hidden', 'true');
      ghost.innerHTML = ghostSvg();
      halloweenSection.appendChild(ghost);
    }

    ghostObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (!entry?.isIntersecting || ghostHasPlayed) return;

        ghostHasPlayed = true;
        ghost.classList.add('is-peeking');
        ghostObserver.disconnect();
      },
      {
        threshold: 0.32,
        rootMargin: '0px 0px -12% 0px'
      }
    );

    ghostObserver.observe(halloweenSection);
  }

  function rebuildAfterThemeEditorChange() {
    window.setTimeout(() => {
      setupGhost();
    }, 120);
  }

  function initialize() {
    if (!isHomepage()) return;
    buildEffects();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, {
      once: true
    });
  } else {
    initialize();
  }

  document.addEventListener(
    'shopify:section:load',
    rebuildAfterThemeEditorChange
  );

  document.addEventListener(
    'shopify:section:reorder',
    rebuildAfterThemeEditorChange
  );
})();
/* =========================================================
   PAWKEMON — SUCCESSFUL ADD TO CART CANDY BURST
   ========================================================= */

(() => {
  'use strict';

  /* 防止 Shopify 主题编辑器重复安装 */
  if (window.__pawkemonCandyBurstInstalled) return;
  window.__pawkemonCandyBurstInstalled = true;

  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const ORIGIN_MAX_AGE = 3000;
  const PARTICLE_COUNT = 8;

  let latestOrigin = null;

  const particleDesigns = [
    { symbol: '🍬', size: 1.00 },
    { symbol: '✦',  size: 0.90 },
    { symbol: '🎃', size: 0.92 },
    { symbol: '🍭', size: 0.95 },
    { symbol: '✦',  size: 0.72 },
    { symbol: '🍬', size: 0.88 },
    { symbol: '🎃', size: 0.78 },
    { symbol: '✧',  size: 0.82 }
  ];

  const directions = [
    { x: -72, y: -64, rotate: -35 },
    { x: -42, y: -92, rotate: 22 },
    { x: -14, y: -110, rotate: -18 },
    { x:  20, y: -106, rotate: 30 },
    { x:  50, y: -88, rotate: -26 },
    { x:  76, y: -58, rotate: 38 },
    { x: -58, y: -35, rotate: -42 },
    { x:  60, y: -30, rotate: 44 }
  ];

  function reducedMotionEnabled() {
    return window.matchMedia &&
      window.matchMedia(REDUCED_MOTION_QUERY).matches;
  }

  function isAddToCartForm(form) {
    if (!form) return false;

    const action = form.getAttribute('action') || '';

    return (
      action.includes('/cart/add') ||
      form.matches('product-form form') ||
      form.closest('product-form') !== null
    );
  }

  function findAddButton(element) {
    if (!(element instanceof Element)) return null;

    const directButton = element.closest(
      'button[type="submit"], input[type="submit"], [name="add"]'
    );

    if (directButton) return directButton;

    const form = element.closest('form');

    return form
      ? form.querySelector(
          'button[name="add"], button[type="submit"], input[type="submit"]'
        )
      : null;
  }

  function rememberOrigin(button) {
    if (!button) return;

    const rect = button.getBoundingClientRect();

    if (rect.width <= 0 || rect.height <= 0) return;

    latestOrigin = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
      time: Date.now()
    };
  }

  function getBurstOrigin() {
    if (
      latestOrigin &&
      Date.now() - latestOrigin.time <= ORIGIN_MAX_AGE
    ) {
      const savedOrigin = latestOrigin;
      latestOrigin = null;
      return savedOrigin;
    }

    /* 找不到按钮时的安全备用位置 */
    return {
      x: window.innerWidth / 2,
      y: Math.min(window.innerHeight - 90, window.innerHeight * 0.78)
    };
  }

  function getBurstLayer() {
    let layer = document.querySelector(
      '.pawkemon-candy-burst-layer'
    );

    if (layer) return layer;

    layer = document.createElement('div');
    layer.className = 'pawkemon-candy-burst-layer';
    layer.setAttribute('aria-hidden', 'true');

    document.body.appendChild(layer);

    return layer;
  }

  function createCandyBurst(origin) {
    if (reducedMotionEnabled()) return;
    if (!document.body) return;

    const layer = getBurstLayer();

    particleDesigns
      .slice(0, PARTICLE_COUNT)
      .forEach((design, index) => {
        const direction = directions[index];
        const particle = document.createElement('span');

        const randomX = Math.round((Math.random() - 0.5) * 14);
        const randomY = Math.round((Math.random() - 0.5) * 10);
        const duration = 720 + Math.round(Math.random() * 110);

        particle.className = 'pawkemon-candy-particle';
        particle.textContent = design.symbol;
        particle.setAttribute('aria-hidden', 'true');

        particle.style.left = `${Math.round(origin.x)}px`;
        particle.style.top = `${Math.round(origin.y)}px`;
        particle.style.fontSize = `${Math.round(22 * design.size)}px`;

        particle.style.setProperty(
          '--burst-x',
          `${direction.x + randomX}px`
        );

        particle.style.setProperty(
          '--burst-y',
          `${direction.y + randomY}px`
        );

        particle.style.setProperty(
          '--burst-rotate',
          `${direction.rotate}deg`
        );

        particle.style.setProperty(
          '--burst-duration',
          `${duration}ms`
        );

        layer.appendChild(particle);

        particle.addEventListener(
          'animationend',
          () => particle.remove(),
          { once: true }
        );

        /* animationend 未触发时的保险清理 */
        window.setTimeout(() => {
          particle.remove();
        }, 1000);
      });
  }

  function requestUrl(resource) {
    if (typeof resource === 'string') return resource;

    if (
      typeof Request !== 'undefined' &&
      resource instanceof Request
    ) {
      return resource.url;
    }

    return resource && typeof resource.url === 'string'
      ? resource.url
      : '';
  }

  function isCartAddRequest(url) {
    if (!url) return false;

    try {
      const parsedUrl = new URL(url, window.location.origin);

      return /\/cart\/add(?:\.js)?\/?$/.test(parsedUrl.pathname);
    } catch (error) {
      return /\/cart\/add(?:\.js)?(?:\?|$)/.test(url);
    }
  }

  /*
   * 记录用户刚刚点击的加购按钮位置。
   * 同时兼容首页商品卡、商品页和 Quick Add。
   */
  document.addEventListener(
    'pointerdown',
    (event) => {
      const button = findAddButton(event.target);

      if (!button || button.disabled) return;

      const form = button.closest('form');

      if (!isAddToCartForm(form)) return;

      rememberOrigin(button);
    },
    true
  );

  /*
   * 键盘 Enter / Space 加购时也能取得按钮位置。
   */
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) return;
      if (!isAddToCartForm(form)) return;

      const button =
        findAddButton(event.submitter) ||
        form.querySelector(
          'button[name="add"], button[type="submit"], input[type="submit"]'
        );

      rememberOrigin(button);
    },
    true
  );

  /*
   * Dawn 16 使用 fetch 调用 Shopify Cart AJAX API。
   * 只有服务器返回成功时才播放糖果效果。
   */
  if (typeof window.fetch === 'function') {
    const originalFetch = window.fetch.bind(window);

    window.fetch = async function pawkemonFetch(resource, options) {
      const url = requestUrl(resource);
      const isAddRequest = isCartAddRequest(url);

      const response = await originalFetch(resource, options);

      if (isAddRequest) {
        if (response.ok) {
          const origin = getBurstOrigin();

          window.requestAnimationFrame(() => {
            createCandyBurst(origin);
          });
        } else {
          /* 加购失败，清除旧位置，不播放动画 */
          latestOrigin = null;
        }
      }

      return response;
    };
  }
})();